import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AmountKeypad } from '@/components/amount-keypad';
import { CategoryGrid } from '@/components/category-grid';
import { AppText } from '@/components/ui/app-text';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { spentGrOf, useBudgetStore } from '@/store/budget-store';
import { colors, radius, spacing } from '@/theme/tokens';
import type { CategoryId } from '@/types/domain';
import { currentMonthNamePl, formatPln } from '@/utils/format';

/** Parse keypad input ("45,50") to grosze; empty/invalid -> 0 */
function parseAmountGr(raw: string): number {
  if (!raw) return 0;
  const [zl, gr = ''] = raw.split(',');
  const grPadded = gr.padEnd(2, '0').slice(0, 2);
  return (parseInt(zl || '0', 10) || 0) * 100 + (parseInt(grPadded || '0', 10) || 0);
}

/**
 * Quick expense entry — mock: wpisz_wydatek/screen.png; must be doable in <5 s.
 * Was a tab; now a stack screen opened by the global "+" FAB, so it carries
 * its own back header.
 */
export default function AddExpenseScreen() {
  const { t } = useTranslation();
  const entries = useBudgetStore((s) => s.entries);
  const monthlyBudgetGr = useBudgetStore((s) => s.monthlyBudgetGr);
  const addEntry = useBudgetStore((s) => s.addEntry);

  // No category pre-selected (owner: "nie zaznaczaj domyślnie kategorii") — the
  // user must actively pick one, so the saved entry reflects a real choice.
  const [category, setCategory] = useState<CategoryId | null>(null);
  const [raw, setRaw] = useState('');
  // Optional label so the expense says WHAT it was ("LEGO", "kino", "McDonald's")
  // — a category alone hid the detail (owner + Maciek).
  const [note, setNote] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  // Owner bug: picking a category left the keypad below the fold and scrolling
  // to it pushed the amount off-screen. The amount block is now pinned above
  // the ScrollView, and picking a category auto-scrolls to the keypad.
  const scrollRef = useRef<ScrollView>(null);
  const keypadY = useRef(0);

  const amountGr = parseAmountGr(raw);
  const spentGr = spentGrOf(entries);
  const leftGr = monthlyBudgetGr - spentGr;
  const spentPct = monthlyBudgetGr > 0 ? Math.max(0, Math.min(100, (spentGr / monthlyBudgetGr) * 100)) : 0;

  const onKey = (key: string) => {
    setSavedFlash(false);
    setRaw((prev) => {
      if (key === 'DEL') return prev.slice(0, -1);
      if (key === ',') {
        if (prev.includes(',') || prev === '') return prev;
        return `${prev},`;
      }
      const [, gr] = prev.split(',');
      if (gr !== undefined && gr.length >= 2) return prev; // max 2 decimal digits
      if (!prev.includes(',') && prev.replace(',', '').length >= 6) return prev; // sane cap
      if (prev === '0' && key === '0') return prev;
      return prev + key;
    });
  };

  const onSave = () => {
    if (!category) return;
    addEntry(category, amountGr, note);
    setRaw('');
    setNote('');
    setSavedFlash(true);
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      {/* Pinned header: back + month bar + the amount being typed. Stays put
          while the list below scrolls to the keypad, so the number the user is
          typing is ALWAYS visible (owner: "nie widać liczby którą wpisujesz"). */}
      <View style={{ paddingHorizontal: spacing.screen, paddingTop: spacing.screen, gap: spacing.md }}>
        {/* Own back header — root Stack has headerShown:false, so without this the
            "+" expense screen is a dead-end (no way back, worst on web). */}
        <ScreenHeader title={t('budget.addTitle')} />

        {/* Month + remaining budget */}
        <View style={{ gap: spacing.xs }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <AppText variant="bodyLg">{currentMonthNamePl()}</AppText>
            <AppText variant="bodyLg">
              {t('budget.left')} <AppText variant="labelLg" style={{ fontSize: 16 }}>{formatPln(leftGr)}</AppText>
            </AppText>
          </View>
          <View style={{ height: 8, borderRadius: radius.full, backgroundColor: colors.primaryTile, overflow: 'hidden' }}>
            <View style={{ height: 8, width: `${spentPct}%`, borderRadius: radius.full, backgroundColor: colors.navy }} />
          </View>
        </View>

        {/* Amount being typed */}
        <View style={{ alignItems: 'center', gap: spacing.base }}>
          <AppText variant="headlineLg" color={colors.navy}>
            − {raw === '' ? '0' : raw} zł
          </AppText>
          <AppText variant="bodyMd">{t('budget.chooseCategory')}</AppText>
        </View>
      </View>

      {/* System keyboard (the note field) must not cover the keypad/save button
          — same KAV pattern as every other form screen in the app. */}
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          ref={scrollRef}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.screen, paddingTop: spacing.md, gap: spacing.lg, paddingBottom: spacing.xl }}
        >
          <CategoryGrid
            selected={category}
            onSelect={(c) => {
              setCategory(c);
              // Owner's flow: pick a category -> the screen slides down to the
              // keypad while the pinned amount stays in view.
              scrollRef.current?.scrollTo({ y: keypadY.current, animated: true });
            }}
          />

          {/* Optional name for the expense — makes a category show what it was on. */}
          <TextField
            label={t('budget.noteLabel')}
            placeholder={t('budget.notePlaceholder')}
            value={note}
            onChangeText={setNote}
          />

          <View onLayout={(e) => { keypadY.current = e.nativeEvent.layout.y; }}>
            <AmountKeypad onKey={onKey} />
          </View>

          <PrimaryButton
            label={savedFlash ? t('budget.savedToast') : t('budget.saveEntry')}
            disabled={amountGr === 0 || category === null}
            onPress={onSave}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
